import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { FileText, AlertCircle, Clock, CheckCircle2, MapPin, ArrowRight } from 'lucide-react';
import { getUser } from '@/utils/auth';
import api, { getBackendUrl, getAssetUrl } from '@/utils/api';
import FileUploader from '@/components/FileUploader';

// Industry Type Options
const INDUSTRY_TYPES = [
  'Pvt Ltd',
  'India Ltd',
  'MSME',
  'Corporate',
  'Limited Liability Partnership (LLP)',
  'Partnership',
  'Proprietorship',
  'Public Limited',
  'Trust/NGO'
];

// Sector Options
const SECTORS = [
  'Agriculture and Allied Industries',
  'Automobiles',
  'Auto Components',
  'Aviation',
  'Banking',
  'Cement',
  'Consumer Durables',
  'E-Commerce',
  'Education and Training',
  'Engineering and Capital Goods',
  'Financial Services',
  'FMCG',
  'Gems and Jewellery',
  'Healthcare',
  'Infrastructure',
  'Insurance',
  'IT & ITES',
  'Logistics',
  'Manufacturing',
  'Media and Entertainment',
  'Metals and Mining',
  'Oil and Gas',
  'Pharmaceuticals',
  'Ports',
  'Power',
  'Railways',
  'Real Estate',
  'Renewable Energy',
  'Retail',
  'Roads',
  'Science and Technology',
  'Services',
  'Steel',
  'Telecommunications',
  'Textiles',
  'Tourism and Hospitality'
];

const CompanyProfile = () => {
  const user = getUser();
  const location = useLocation();
  const navigate = useNavigate();
  const isNewRegistration = location.state?.newRegistration;
  const [loading, setLoading] = useState(false);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [districtSuggestions, setDistrictSuggestions] = useState([]);
  const [showDistrictSuggestions, setShowDistrictSuggestions] = useState(false);
  const [districtSearchInput, setDistrictSearchInput] = useState('');
  const districtInputRef = useRef(null);
  const [showApprovalNotice, setShowApprovalNotice] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState(user?.approval_status || 'pending');
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;
  
  const [profile, setProfile] = useState({
    company_name: '',
    service_type: '',
    industry_type: '',
    industry_sector: '',
    company_info: '',
    address: '',
    main_mobile: user?.mobile || '',
    email: '',
    num_employees: '',
    website: '',
    logo: '',
    id_proof: '',
    state: '',
    district: '',
    city: '',
    contact_person_name: ''
  });

  useEffect(() => {
    fetchProfile();
    fetchLocations();
    fetchUserStatus();
  }, []);

  const fetchUserStatus = async () => {
    try {
      const response = await api.get(`/auth/user/${user.id}`);
      if (response.data) {
        setApprovalStatus(response.data.approval_status || 'approved');
      }
    } catch (error) {
      console.error('Error fetching user status:', error);
    }
  };

  const fetchLocations = async () => {
    try {
      const statesRes = await api.get('/location-master/states');
      setStates(statesRes.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  // Search districts with autocomplete
  const searchDistricts = async (query) => {
    if (query.length < 2) {
      setDistrictSuggestions([]);
      setShowDistrictSuggestions(false);
      return;
    }
    
    try {
      const response = await api.get(`/location-master/districts/search?q=${encodeURIComponent(query)}&limit=15`);
      setDistrictSuggestions(response.data);
      setShowDistrictSuggestions(response.data.length > 0);
    } catch (error) {
      console.error('Error searching districts:', error);
    }
  };

  const handleDistrictSearchChange = (e) => {
    const value = e.target.value;
    setDistrictSearchInput(value);
    searchDistricts(value);
  };

  const selectDistrictSuggestion = (suggestion) => {
    setProfile({
      ...profile,
      state: suggestion.state,
      district: suggestion.district
    });
    setDistrictSearchInput(suggestion.display);
    setDistrictSuggestions([]);
    setShowDistrictSuggestions(false);
  };

  // Fetch districts when state changes
  const fetchDistrictsByState = async (state) => {
    try {
      const response = await api.get(`/location-master/districts?state=${encodeURIComponent(state)}`);
      setDistricts(response.data);
    } catch (error) {
      console.error('Error fetching districts:', error);
    }
  };

  const handleStateChange = (value) => {
    setProfile({...profile, state: value, district: ''});
    setDistrictSearchInput('');
    fetchDistrictsByState(value);
  };

  const fetchProfile = async () => {
    try {
      const response = await api.get(`/employer/profile/${user.id}`);
      // API returns company data directly (not nested under company_profile)
      if (response.data && response.data.company_name) {
        const companyData = response.data;
        setProfile({
          company_name: companyData.company_name || '',
          service_type: companyData.service_type || '',
          industry_type: companyData.industry_type || '',
          industry_sector: companyData.industry_sector || '',
          company_info: companyData.company_info || '',
          address: companyData.address || '',
          main_mobile: companyData.main_mobile || user?.mobile || '',
          email: companyData.email || '',
          num_employees: companyData.num_employees || '',
          website: companyData.website || '',
          logo: companyData.logo || '',
          id_proof: companyData.id_proof || '',
          state: companyData.state || '',
          district: companyData.district || '',
          city: companyData.city || '',
          contact_person_name: companyData.contact_person_name || ''
        });
        // Set district search input if district exists
        if (companyData.state && companyData.district) {
          setDistrictSearchInput(`${companyData.state} / ${companyData.district}`);
        }
        // If profile exists with company name, show all steps
        setCurrentStep(3);
      } else {
        setProfile(prev => ({ ...prev, main_mobile: user?.mobile || '' }));
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setProfile(prev => ({ ...prev, main_mobile: user?.mobile || '' }));
    }
  };

  // Handle logo upload success
  const handleLogoUploadSuccess = (path) => {
    setProfile({ ...profile, logo: path });
  };

  // Handle ID proof upload success
  const handleIdProofUploadSuccess = (path) => {
    setProfile({ ...profile, id_proof: path });
  };

  const validateStep1 = () => {
    // Company Name validation
    if (!profile.company_name || profile.company_name.trim().length < 3) {
      toast.error('Company Name is required (minimum 3 characters)');
      return false;
    }
    // Contact Person Name validation (mandatory)
    if (!profile.contact_person_name || profile.contact_person_name.trim().length < 2) {
      toast.error('Contact Person Name is required (minimum 2 characters)');
      return false;
    }
    if (!profile.industry_type) {
      toast.error('Industry Type is required');
      return false;
    }
    if (!profile.industry_sector) {
      toast.error('Sector is required');
      return false;
    }
    if (!profile.num_employees) {
      toast.error('Number of Employees is required');
      return false;
    }
    // Email validation
    if (!profile.email || profile.email.trim() === '') {
      toast.error('Email ID is required');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(profile.email)) {
      toast.error('Please enter a valid email address');
      return false;
    }
    // Mobile validation
    if (!profile.main_mobile || profile.main_mobile.trim() === '') {
      toast.error('Mobile Number is required');
      return false;
    }
    const mobileRegex = /^[6-9]\d{9}$/;
    if (!mobileRegex.test(profile.main_mobile.replace(/\D/g, ''))) {
      toast.error('Please enter a valid 10-digit mobile number');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!profile.state || profile.state.trim() === '') {
      toast.error('State is required');
      return false;
    }
    if (!profile.district || profile.district.trim() === '') {
      toast.error('District is required');
      return false;
    }
    if (!profile.address || profile.address.trim().length < 10) {
      toast.error('Address is required (minimum 10 characters)');
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (!profile.id_proof) {
      toast.error('Please upload a valid Employer ID');
      return false;
    }
    return true;
  };

  const handleSaveAndNext = async () => {
    if (currentStep === 1 && !validateStep1()) return;
    if (currentStep === 2 && !validateStep2()) return;
    
    setLoading(true);
    try {
      const response = await api.put(`/employer/profile/${user.id}`, profile);
      // Update local state with the saved data
      if (response.data) {
        setProfile({
          company_name: response.data.company_name || '',
          service_type: response.data.service_type || '',
          industry_type: response.data.industry_type || '',
          industry_sector: response.data.industry_sector || '',
          company_info: response.data.company_info || '',
          address: response.data.address || '',
          main_mobile: response.data.main_mobile || user?.mobile || '',
          email: response.data.email || '',
          num_employees: response.data.num_employees || '',
          website: response.data.website || '',
          logo: response.data.logo || '',
          id_proof: response.data.id_proof || '',
          state: response.data.state || '',
          district: response.data.district || '',
          city: response.data.city || '',
          contact_person_name: response.data.contact_person_name || ''
        });
      }
      toast.success('Progress saved!');
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      }
    } catch (error) {
      toast.error('Failed to save progress');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep1() || !validateStep2() || !validateStep3()) return;
    
    setLoading(true);
    try {
      const response = await api.put(`/employer/profile/${user.id}`, profile);
      
      // Update local state with the saved data
      if (response.data) {
        setProfile({
          company_name: response.data.company_name || '',
          service_type: response.data.service_type || '',
          industry_type: response.data.industry_type || '',
          industry_sector: response.data.industry_sector || '',
          company_info: response.data.company_info || '',
          address: response.data.address || '',
          main_mobile: response.data.main_mobile || user?.mobile || '',
          email: response.data.email || '',
          num_employees: response.data.num_employees || '',
          website: response.data.website || '',
          logo: response.data.logo || '',
          id_proof: response.data.id_proof || '',
          state: response.data.state || '',
          district: response.data.district || '',
          city: response.data.city || '',
          contact_person_name: response.data.contact_person_name || ''
        });
      }
      
      toast.success('Company profile saved successfully! Redirecting to dashboard...');
      
      // Show approval notice briefly if pending, then redirect
      if (approvalStatus === 'pending') {
        setShowApprovalNotice(true);
        // Redirect to dashboard after showing the notice briefly
        setTimeout(() => {
          navigate('/employer/dashboard');
        }, 2000);
      } else {
        // If already approved, redirect immediately
        setTimeout(() => {
          navigate('/employer/dashboard');
        }, 1000);
      }
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3].map((step) => (
        <div key={step} className="flex items-center">
          <div 
            className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
              currentStep >= step 
                ? 'bg-teal-600 text-white' 
                : 'bg-slate-200 text-slate-500'
            }`}
          >
            {step}
          </div>
          {step < 3 && (
            <div className={`w-16 h-1 ${currentStep > step ? 'bg-teal-600' : 'bg-slate-200'}`} />
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div>
      <h1 className="text-3xl font-bold text-slate-900 mb-6" style={{fontFamily: 'Outfit'}}>Company Profile</h1>
      
      {/* New Registration Notice */}
      {isNewRegistration && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-blue-800">Welcome! Please Complete Your Profile</h3>
            <p className="text-sm text-blue-700 mt-1">
              Fill in your company details and upload an ID proof for verification. Your profile will be reviewed by admin for approval.
            </p>
          </div>
        </div>
      )}

      {/* Pending Approval Notice */}
      {approvalStatus === 'pending' && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
          <Clock className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-amber-800">Pending Admin Approval</h3>
            <p className="text-sm text-amber-700 mt-1">
              Your profile is waiting for admin approval. Once approved, you will be able to post jobs and manage HR accounts.
              Please ensure you have uploaded a valid ID proof for faster approval.
            </p>
          </div>
        </div>
      )}

      {/* Profile Saved Approval Notice */}
      {showApprovalNotice && approvalStatus === 'pending' && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
          <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-green-800">Profile Saved Successfully!</h3>
            <p className="text-sm text-green-700 mt-1">
              Your profile has been saved and is waiting for admin approval. Once approved, you will be able to:
            </p>
            <ul className="text-sm text-green-700 mt-2 list-disc list-inside">
              <li>Post job vacancies</li>
              <li>Create and manage HR executive accounts</li>
              <li>View and manage job applications</li>
            </ul>
          </div>
        </div>
      )}
      
      {/* Step Indicator */}
      {renderStepIndicator()}
      
      <Card className="p-8 border-slate-200">
        <form onSubmit={handleSubmit} className="space-y-6">
          
          {/* Step 1: Company Information */}
          {currentStep >= 1 && (
            <div className={currentStep === 1 ? '' : 'opacity-60'}>
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-teal-600 text-white text-sm flex items-center justify-center">1</span>
                Company Information
              </h2>
              
              <FileUploader
                label="Company Logo"
                accept="image/*"
                uploadEndpoint="/upload/logo"
                currentFile={profile.logo}
                onUploadSuccess={handleLogoUploadSuccess}
                onRemove={() => setProfile({...profile, logo: ''})}
                maxSizeMB={1}
                helpText="Logo will be auto-cropped to a 1:1 square. You can pick Center Crop or White-padded preview before uploading."
                showPreview={true}
                previewType="image"
                squareCrop={true}
              />

              <div className="grid md:grid-cols-2 gap-6 mt-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Company Name <span className="text-red-500">*</span></label>
                  <Input
                    data-testid="company-name-input"
                    value={profile.company_name}
                    onChange={(e) => setProfile({...profile, company_name: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Industry Type <span className="text-red-500">*</span></label>
                  <Select value={profile.industry_type} onValueChange={(value) => setProfile({...profile, industry_type: value})}>
                    <SelectTrigger data-testid="industry-type-select">
                      <SelectValue placeholder="Select industry type" />
                    </SelectTrigger>
                    <SelectContent>
                      {INDUSTRY_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Sector <span className="text-red-500">*</span></label>
                  <Select value={profile.industry_sector} onValueChange={(value) => setProfile({...profile, industry_sector: value})}>
                    <SelectTrigger data-testid="industry-sector-select">
                      <SelectValue placeholder="Select sector" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {SECTORS.map((sector) => (
                        <SelectItem key={sector} value={sector}>{sector}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Number of Employees <span className="text-red-500">*</span></label>
                  <Input
                    data-testid="num-employees-input"
                    type="number"
                    value={profile.num_employees}
                    onChange={(e) => setProfile({...profile, num_employees: e.target.value})}
                    required
                  />
                </div>
                <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Main Mobile Number <span className="text-red-500">*</span></label>
                <Input
                  data-testid="company-mobile-input"
                  type="tel"
                  value={profile.main_mobile}
                  onChange={(e) => setProfile({...profile, main_mobile: e.target.value})}
                  placeholder="Enter mobile number"
                  required
                />
                <p className="text-xs text-slate-500 mt-1">Primary contact number for the company</p>
              </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Contact Person Name <span className="text-red-500">*</span></label>
                  <Input
                    data-testid="contact-person-input"
                    value={profile.contact_person_name}
                    onChange={(e) => setProfile({...profile, contact_person_name: e.target.value})}
                    placeholder="Name of contact person"
                    required
                  />
                  <p className="text-xs text-slate-500 mt-1">Name of the person who can be contacted for hiring queries</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Email ID <span className="text-red-500">*</span></label>
                  <Input
                    data-testid="company-email-input"
                    type="email"
                    placeholder="company@example.com"
                    value={profile.email}
                    onChange={(e) => setProfile({...profile, email: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Website <span className="text-slate-400">(Optional)</span></label>
                  <Input
                    data-testid="company-website-input"
                    type="url"
                    placeholder="https://www.example.com"
                    value={profile.website}
                    onChange={(e) => setProfile({...profile, website: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="mt-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">Company Info <span className="text-red-500">*</span></label>
                <Textarea
                  data-testid="company-info-textarea"
                  rows={4}
                  value={profile.company_info}
                  onChange={(e) => setProfile({...profile, company_info: e.target.value})}
                  placeholder="Brief description about your company..."
                  required
                />
              </div>

              {currentStep === 1 && (
                <div className="mt-6 flex justify-end">
                  <Button 
                    type="button"
                    onClick={handleSaveAndNext}
                    className="bg-teal-600 hover:bg-teal-700"
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Save & Next'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Location & Address */}
          {currentStep >= 2 && (
            <div className={`mt-8 pt-8 border-t ${currentStep === 2 ? '' : 'opacity-60'}`}>
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-teal-600 text-white text-sm flex items-center justify-center">2</span>
                Location & Address
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">State <span className="text-red-500">*</span></label>
                  <Select value={profile.state} onValueChange={handleStateChange}>
                    <SelectTrigger data-testid="company-state-select">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {states.map((state) => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">District <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <Input
                      ref={districtInputRef}
                      data-testid="company-district-input"
                      placeholder="Type to search district..."
                      value={districtSearchInput || profile.district}
                      onChange={handleDistrictSearchChange}
                      onFocus={() => {
                        if (districtSearchInput.length >= 2) {
                          setShowDistrictSuggestions(true);
                        }
                      }}
                    />
                    {showDistrictSuggestions && districtSuggestions.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {districtSuggestions.map((suggestion, index) => (
                          <button
                            key={index}
                            type="button"
                            className="w-full px-4 py-2 text-left hover:bg-slate-50 focus:bg-slate-50 focus:outline-none border-b border-slate-100 last:border-b-0"
                            onClick={() => selectDistrictSuggestion(suggestion)}
                          >
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-slate-400" />
                              <span className="text-sm">{suggestion.display}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {profile.state && !districtSearchInput && (
                    <Select 
                      value={profile.district} 
                      onValueChange={(value) => {
                        setProfile({...profile, district: value});
                        setDistrictSearchInput(value);
                      }}
                    >
                      <SelectTrigger className="mt-2" data-testid="company-district-select">
                        <SelectValue placeholder="Or select from list" />
                      </SelectTrigger>
                      <SelectContent>
                        {districts.map((d) => (
                          <SelectItem key={d.district} value={d.district}>{d.district}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">City/Area <span className="text-slate-400">(Optional)</span></label>
                  <Input
                    data-testid="company-city-input"
                    placeholder="Enter city or area name"
                    value={profile.city}
                    onChange={(e) => setProfile({...profile, city: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="mt-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">Address <span className="text-red-500">*</span></label>
                <Textarea
                  data-testid="company-address-textarea"
                  rows={3}
                  value={profile.address}
                  onChange={(e) => setProfile({...profile, address: e.target.value})}
                  placeholder="Full company address..."
                  required
                />
              </div>

              {currentStep === 2 && (
                <div className="mt-6 flex justify-between">
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={() => setCurrentStep(1)}
                  >
                    Back
                  </Button>
                  <Button 
                    type="button"
                    onClick={handleSaveAndNext}
                    className="bg-teal-600 hover:bg-teal-700"
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Save & Next'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Step 3: ID Verification */}
          {currentStep >= 3 && (
            <div className={`mt-8 pt-8 border-t ${currentStep === 3 ? '' : 'opacity-60'}`}>
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-teal-600 text-white text-sm flex items-center justify-center">3</span>
                ID Verification
              </h2>

              {/* ID Proof Upload Section */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2 mb-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-amber-800">Upload Valid Employer ID <span className="text-red-500">*</span></h4>
                    <p className="text-sm text-amber-700 mt-1">
                      Upload your Employee ID Card, Business Card, or any valid employer identification as a photo copy. This helps us verify your business authenticity.
                    </p>
                  </div>
                </div>
                
                <FileUploader
                  label=""
                  accept="image/*,.pdf"
                  uploadEndpoint="/upload/id-proof"
                  currentFile={profile.id_proof}
                  onUploadSuccess={handleIdProofUploadSuccess}
                  onRemove={() => setProfile({...profile, id_proof: ''})}
                  maxSizeMB={1}
                  helpText="Accepted: Employee ID Card, Business Card as photo copy (JPG, PNG, PDF - Max 1MB)"
                  showPreview={true}
                  previewType={profile.id_proof?.toLowerCase()?.endsWith('.pdf') ? 'document' : 'image'}
                />
              </div>

              <div className="mt-6 flex justify-between">
                <Button 
                  type="button"
                  variant="outline"
                  onClick={() => setCurrentStep(2)}
                >
                  Back
                </Button>
                <Button 
                  data-testid="save-company-profile-btn"
                  type="submit" 
                  className="bg-teal-600 hover:bg-teal-700"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Submit Profile'}
                </Button>
              </div>
            </div>
          )}
        </form>
      </Card>
    </div>
  );
};

export default CompanyProfile;
